import React, { useState, useContext, useEffect, useMemo } from "react";
import { get, map } from 'lodash';
import { formatCompletionDate } from "../../../Utils/BusinessLib";
import { useNavigate } from 'react-router-dom';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { DataGridWrapper } from "../../../Utils/DataGridWrapper/DataGridWrapper";
import { GridReactContext } from "../../../Utils/DataGridWrapper/GridContext";
import { I18nReactContext } from "../../../contexts/I18nContext";
import { getSingleSignDocUsersApi, approveOrRejectUserUploadAPI, markSignDocUserAsCompletedAPI, unmarkSignDocUserAsCompletedAPI, getSingleSignDocUsersAsExcelAPI } from "../../../Services/api";
import { UserSurveyAnswersCell } from "../UserSurveyAnswersCell/UserSurveyAnswersCell";
import './SignDocUsersTable.scss';
import { SignDocTypes } from "../../SignDoc/SignDocLib";
import { SignDocApprovalActions } from "../../SignDoc/SignDocApprovalActions/SignDocApprovalActions";
import { StatusAndCompleteCell } from "../../Users/StatusAndCompleteCell/StatusAndCompleteCell";

/**
 * Gets the columns configuration for the SignDoc users table
 * @param {object} params - Function parameters
 * @param {string} params.signDocId - The SignDoc ID
 * @param {string} params.organizationId - The organization ID
 * @param {object} params.i18n - Internationalization object
 * @param {boolean} params.isAnonymous - Whether the survey is anonymous
 * @param {string} [params.signDocType] - The SignDoc type (to determine if answers column should be shown)
 * @param {Array|null} [params.learningPathChildren] - Array of child SignDocs for LEARNING_PATH type [{id, docName, type, order}]
 * @param {Function} [params.handleApproveAction] - Handler for approve action
 * @param {Function} [params.handleRejectAction] - Handler for reject action
 * @param {Function} [params.markCompleted] - Handler to mark sign doc as completed
 * @param {Function} [params.unmarkCompleted] - Handler to unmark sign doc completion
 * @returns {Array} Array of column configurations
 */
const getUsersColumns = ({ signDocId, i18n, isAnonymous, signDocType, learningPathChildren, handleApproveAction, handleRejectAction, markCompleted, unmarkCompleted }) => {
    // Types that have questions/answers: EXAM and SURVEY
    const hasAnswers = signDocType === SignDocTypes.EXAM || signDocType === SignDocTypes.SURVEY;
    const isCourse = signDocType === SignDocTypes.ONLINE_COURSE || signDocType === SignDocTypes.FRONTAL_COURSE;

    const columns = [
        {
            field: 'firstName',
            headerName: i18n.translate("surveyUsers.columns.fullName"),
            minWidth: 160,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{`${params.value} ${params.row.lastName}`}</div>,
        },
        {
            field: 'idNumber',
            headerName: i18n.translate("surveyUsers.columns.idNumber"),
            minWidth: 140,
            align: 'right',
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value || '-'}</div>,
        },
        {
            field: 'employeeId',
            headerName: i18n.translate("surveyUsers.columns.employeeId"),
            minWidth: 140,
            align: 'right',
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value || '-'}</div>,
        },
        {
            field: 'status',
            headerName: i18n.translate("surveyUsers.columns.status"),
            minWidth: 250,
            align: 'right',
            sortable: true,
            renderCell: (params) => {
                const userSignDocData = get(params, 'row.signDocData', {});
                const userId = get(params, 'row.id');

                // Build signDoc object in the format StatusAndCompleteCell expects
                const signDocForCell = {
                    id: signDocId,
                    type: signDocType,
                    progressPercent: get(userSignDocData, 'progressPercent', 0),
                    viewed: get(userSignDocData, 'viewed', false),
                    signed: get(userSignDocData, 'signed', false),
                    completedOnDate: get(userSignDocData, 'completedOnDate', null),
                    completedByUserId: get(userSignDocData, 'completedByUserId', null),
                    examScore: get(userSignDocData, 'examScore', null),
                    outcomeStatus: get(userSignDocData, 'outcomeStatus', null),
                    category: get(userSignDocData, 'signDoc.category', null),
                };

                return (
                    <StatusAndCompleteCell
                        signDoc={signDocForCell}
                        i18n={i18n}
                        markCompleted={(signDocIdParam) => markCompleted(signDocIdParam, userId)}
                        unmarkCompleted={(signDocIdParam) => unmarkCompleted(signDocIdParam, userId)}
                        isSignDocValid={true}
                        showCompletionDate={true}
                    />
                );
            },
        }
    ];

    // Only add answers column for types that have questions (EXAM, SURVEY)
    if (hasAnswers) {
        columns.push({
            field: 'showUserAnswer',
            headerName: i18n.translate("surveyUsers.columns.showUserAnswer"),
            minWidth: 150,
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                const completedOnDate = get(params, 'row.signDocData.completedOnDate');
                const userId = get(params, 'row.id');
                const user = params.row;

                return (
                    <UserSurveyAnswersCell
                        user={user}
                        userId={userId}
                        surveyId={signDocId}
                        completedOnDate={completedOnDate}
                        isAnonymous={isAnonymous}
                        i18n={i18n}
                        includeCorrectAnswer={signDocType === SignDocTypes.EXAM}
                    />
                );
            },
        });
    }

    // Add dynamic columns for each child SignDoc in a Learning Path
    if (learningPathChildren && learningPathChildren.length > 0) {
        learningPathChildren.forEach((child) => {
            columns.push({
                field: `child_${child.id}`,
                headerName: child.docName,
                minWidth: 220,
                align: 'right',
                sortable: false,
                renderCell: (params) => {
                    const childStatuses = get(params, 'row.childStatuses', {});
                    const status = childStatuses[child.id] || 'not_started';
                    const statusTextMap = {
                        'completed': i18n.translate('learningPathReport.status.completed'),
                        'in_progress': i18n.translate('learningPathReport.status.inProgress'),
                        'not_started': i18n.translate('learningPathReport.status.notStarted')
                    };
                    // Each task in the path carries its own completion date, same as the path row itself
                    const completionDateText = formatCompletionDate({
                        value: get(params, `row.childCompletionDates.${child.id}`, null)
                    });

                    return (
                        <div style={{width: '100%'}}>
                            <div>{statusTextMap[status]}</div>
                            {completionDateText && <div className="childCompletionDate">{completionDateText}</div>}
                        </div>
                    );
                },
            });
        });
    }

    // Add actions column for courses (if current user is an approver)
    if (isCourse) {
        columns.push({
            field: 'actions',
            headerName: i18n.translate("deletedUserModal.columns.actions"),
            minWidth: 120,
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                const userSignDocData = get(params, 'row.signDocData', {});
                const isCurrentUserApprover = get(userSignDocData, 'signDoc.isCurrentUserApprover', false);
                const userSignDocId = get(userSignDocData, 'id');

                return (
                    <SignDocApprovalActions
                        row={params.row}
                        signDocType={signDocType}
                        isCurrentUserApprover={isCurrentUserApprover}
                        onApprove={() => handleApproveAction(userSignDocId)}
                        onReject={() => handleRejectAction(userSignDocId)}
                    />
                );
            },
        });
    }

    return columns;
};

const tableFilters = {
    showFilters: {
        search: true,
        organizations: true // Enable organization filter to show alongside search
    },
    filterValues: {},
    filterData: {
        organizations: [] // Will be populated with admin organizations
    }
};

const paginationModel = {
    page: 0,
    pageSize: 20,
};

// Default sort model - no initial sorting
const defaultSortModel = [];

/**
 * Converts user data for display in the data grid
 * @param {Array} users - Array of user objects
 * @returns {Array} Converted user data
 */
const convertData = (users) => {
    return map(users, (user) => {
        const userSignDoc = get(user, 'userSignDoc', {});
        return {
            ...user,
            signDocData: userSignDoc,
            childStatuses: get(user, 'childStatuses', {}),
            childCompletionDates: get(user, 'childCompletionDates', {}),
        };
    });
};

/**
 * SignDocUsersTable Component
 *
 * Displays a table of users and their status for a specific SignDoc (survey, exam, course, etc.).
 * Shows user info (firstName, lastName, identifier) and status.
 * For types with questions (EXAM, SURVEY), also shows answers column.
 *
 * @param {object} params - Component parameters
 * @param {string} params.signDocId - The SignDoc ID to show users for
 * @param {Array<string>} params.organizationIds - Array of organization IDs to filter users by
 * @param {object} [params.signDocData] - The complete SignDoc data object from parent (includes organization assignments, questionnaire data, etc.)
 * @param {Array} [params.adminOrganizationOptions] - Array of organization options for the filter dropdown (all admin-managed organizations)
 * @param {Function} [params.onOrganizationFilterChange] - Callback when organization filter changes
 * @param {Function} [params.onReportDataChanged] - Callback when a row's completion changed, so the header stats can be re-read
 * @returns {JSX.Element} Rendered SignDoc users table component
 */
export const SignDocUsersTable = ({ signDocId, organizationIds, signDocData, adminOrganizationOptions, onOrganizationFilterChange, onReportDataChanged }) => {
    const { i18n } = useContext(I18nReactContext);
    const gridContext = useContext(GridReactContext);
    const navigate = useNavigate();
    const [isExporting, setIsExporting] = useState(false);

    /**
     * Refreshes the table rows and the header stats above it after a completion change.
     * The grid context only reaches the table, so the header has to be told separately.
     * @returns {void}
     */
    const refreshAfterCompletionChange = () => {
        gridContext.triggerTableRefresh();
        if (onReportDataChanged) {
            onReportDataChanged();
        }
    };

    /**
     * Calculate filtered organizations: intersection of admin-managed organizations and SignDoc-assigned organizations
     * This ensures the filter only shows organizations that:
     * 1. The admin manages
     * 2. The SignDoc is actually assigned to
     */
    const filteredOrganizationOptions = useMemo(() => {
        if (!signDocData || !adminOrganizationOptions) {
            return adminOrganizationOptions || [];
        }

        // Get SignDoc assigned organization IDs
        const signDocOrgIds = get(signDocData, 'organizationSignDoc', []).map(orgSignDoc =>
            get(orgSignDoc, 'OrganizationId')
        ).filter(Boolean);

        // If no organizations assigned to SignDoc, return empty (shouldn't happen in practice)
        if (signDocOrgIds.length === 0) {
            return [];
        }

        // Filter admin organizations to only include those assigned to this SignDoc
        return adminOrganizationOptions.filter(option => {
            // Always include 'all' option
            if (option.value === 'all') {
                return true;
            }
            // Include if organization is assigned to this SignDoc
            return signDocOrgIds.includes(option.value);
        });
    }, [signDocData, adminOrganizationOptions]);

    /**
     * Handler for approving a user's course attendance (marking as attended)
     * @param {string} userSignDocId - The UserSignDoc ID to approve
     * @returns {Promise<void>}
     */
    const handleApproveAction = async (userSignDocId) => {
        try {
            await approveOrRejectUserUploadAPI({
                userSignDocId,
                expiryDate: null,
                isApproved: true
            });
            refreshAfterCompletionChange();
        } catch (error) {
            console.error('Error marking user as attended:', error);
        }
    };

    /**
     * Handler for rejecting a user's course attendance (marking as no-show)
     * @param {string} userSignDocId - The UserSignDoc ID to reject
     * @returns {Promise<void>}
     */
    const handleRejectAction = async (userSignDocId) => {
        try {
            await approveOrRejectUserUploadAPI({
                userSignDocId,
                expiryDate: null,
                isApproved: false
            });
            refreshAfterCompletionChange();
        } catch (error) {
            console.error('Error marking user as no-show:', error);
        }
    };

    /**
     * Handler for marking a sign doc as completed for a user
     * @param {string} signDocIdParam - The SignDoc ID
     * @param {string} userId - The User ID
     * @returns {Promise<void>}
     */
    const markCompleted = async (signDocIdParam, userId) => {
        try {
            await markSignDocUserAsCompletedAPI({ signDocId: signDocIdParam, userId });
            refreshAfterCompletionChange();
        } catch (error) {
            console.error('Error marking sign doc as completed:', error);
        }
    };

    /**
     * Handler for unmarking a sign doc completion for a user
     * @param {string} signDocIdParam - The SignDoc ID
     * @param {string} userId - The User ID
     * @returns {Promise<void>}
     */
    const unmarkCompleted = async (signDocIdParam, userId) => {
        try {
            await unmarkSignDocUserAsCompletedAPI({ signDocId: signDocIdParam, userId });
            refreshAfterCompletionChange();
        } catch (error) {
            console.error('Error unmarking sign doc completion:', error);
        }
    };

    /**
     * Handles downloading the report as Excel file.
     * Uses a dedicated single-SignDoc export endpoint that produces a single flat sheet.
     * @returns {Promise<void>}
     */
    const handleDownloadReport = async () => {
        try {
            setIsExporting(true);
            await getSingleSignDocUsersAsExcelAPI({
                signDocId,
                organizationIds: organizationIds || ['all']
            });
            navigate('/reports/generatedReports');
        } catch (error) {
            console.error('Error generating report:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const tableButtons = [{
        text: i18n.translate('courseReports.downloadReport'),
        icon: <CloudDownloadIcon />,
        onClick: handleDownloadReport,
        disabled: isExporting,
        loading: isExporting,
        color: 'yellow'
    }];

    const [localTableFilters, setLocalTableFilters] = useState({
        ...tableFilters,
        filterValues: {
            ...tableFilters.filterValues,
            signDocId,
            organizationIds: organizationIds || ['all'] // Default to all if not provided
        },
        filterData: {
            ...tableFilters.filterData,
            organizations: filteredOrganizationOptions
        }
    });

    // Update filter data when filteredOrganizationOptions change
    useEffect(() => {
        if (filteredOrganizationOptions && filteredOrganizationOptions.length > 0) {
            setLocalTableFilters(prev => ({
                ...prev,
                filterData: {
                    ...prev.filterData,
                    organizations: filteredOrganizationOptions
                }
            }));
        }
    }, [filteredOrganizationOptions]);

    /**
     * Gets the columns configuration for the table
     * @param {object} params - Function parameters
     * @param {object} params.paginationModel - Pagination settings
     * @param {object} params.filterModel - Filter settings
     * @param {object} params.sortModel - Sort settings
     * @returns {Promise<Array>} Promise that resolves to array of column configurations
     */
    const getColumns = async ({paginationModel, filterModel, sortModel}) => {
        return getUsersColumns({
            signDocId,
            i18n,
            isAnonymous: get(signDocData, 'isAnonymous', false),
            signDocType: get(signDocData, 'type', ''),
            learningPathChildren: get(signDocData, 'learningPathChildren', null),
            handleApproveAction,
            handleRejectAction,
            markCompleted,
            unmarkCompleted
        });
    };

    /**
     * API call to get user data for the SignDoc with search and sort functionality
     * @param {object} params - Function parameters
     * @param {object} params.paginationModel - Pagination settings
     * @param {object} params.filterModel - Filter settings (includes search term and organizationIds)
     * @param {object} params.sortModel - Sort settings
     * @returns {Promise} API response with user data
     */
    const apiToGetData = async ({ paginationModel, filterModel, sortModel }) => {
        // Get organizationIds from the filter model (DataGridWrapper's internal state)
        const selectedOrgIds = get(filterModel, 'filterValues.organizationIds', organizationIds || ['all']);

        // Notify parent component when organization filter changes
        if (onOrganizationFilterChange && JSON.stringify(selectedOrgIds) !== JSON.stringify(organizationIds)) {
            onOrganizationFilterChange(selectedOrgIds);
        }

        return getSingleSignDocUsersApi({
            signDocId,
            organizationIds: selectedOrgIds,
            paginationModel,
            filterModel,
            sortModel
        });
    };

    return (
        <div className="SignDocUsersTable">
            <DataGridWrapper
                filterModel={localTableFilters}
                disableColumnFilter
                paginationModel={paginationModel}
                getColumns={getColumns}
                apiToGetData={apiToGetData}
                dataConverter={convertData}
                sortModel={defaultSortModel}
                buttons={tableButtons}
                checkboxSelection={false}
                onSelectionModelChange={null}
                selectionModel={[]}
                isRowSelectable={null}
            />
        </div>
    );
};
